import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import moment from 'moment';
import { embedSignatureImage, drawSignatureImage } from '../../../../../helpers/embedSignatureImage';
import { fetchPdfBytes } from '../../../../../helpers/fetchPdfBytes';

const drawText = (page: any, text: string, coords: { x: number; y: number; size?: number }, font: any) => {
  if (!text) return;
  page.drawText(text, {
    x: coords.x,
    y: coords.y,
    size: coords.size ?? 11,
    font,
    color: rgb(0, 0, 0),
  });
};

const drawSignature = async (
  pdfDoc: any,
  page: any,
  pngBase64: string,
  coords: { x: number; y: number; height: number; opacity?: number },
) => {
  const png = await embedSignatureImage(pdfDoc, pngBase64);
  drawSignatureImage(page, png, coords);
};

const applyEraseRectangles = (
  pages: any[],
  rects?: Array<{ page: number; x: number; y: number; width: number; height: number }>,
) => {
  if (!rects) return;
  for (const r of rects) {
    const page = pages[r.page];
    if (!page) continue;
    page.drawRectangle({ x: r.x, y: r.y, width: r.width, height: r.height, color: rgb(1, 1, 1), opacity: 1 });
  }
};

export interface EmployeeChildcareTuitionData {
  employeeName: string;
  childrenEnrolled: string;
  preferredPaymentMethod: 'Credit Card' | 'Debit Card' | 'ACH' | '';
  signatureDate: string;
}

export const buildEmployeeChildcareTuitionPdf = async (
  pdfUrl: string,
  config: any,
  data: EmployeeChildcareTuitionData,
  signaturePng?: string,
): Promise<Uint8Array> => {
  const arrayBuffer = await fetchPdfBytes(pdfUrl);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();

  applyEraseRectangles(pages, config.eraseRectangles);

  const fields = config.coordinatesFields || {};
  if (fields.employeeName) drawText(pages[fields.employeeName.page], data.employeeName, fields.employeeName, font);
  if (fields.childrenEnrolled)
    drawText(pages[fields.childrenEnrolled.page], data.childrenEnrolled, fields.childrenEnrolled, font);
  if (fields.preferredPaymentMethod)
    drawText(
      pages[fields.preferredPaymentMethod.page],
      data.preferredPaymentMethod,
      fields.preferredPaymentMethod,
      font,
    );
  if (fields.signatureDate) {
    const date = data.signatureDate ? moment(data.signatureDate).format('MM/DD/YYYY') : moment().format('MM/DD/YYYY');
    drawText(pages[fields.signatureDate.page], date, fields.signatureDate, font);
  }

  if (signaturePng) {
    const signPage = pages[config.signPage ?? 1];
    if (signPage) await drawSignature(pdfDoc, signPage, signaturePng, config.coordinates);
  }

  return pdfDoc.save();
};

export interface LaptopContractData {
  employeeName: string;
  initials: string;
  noticeAddress: string;
  noticeCityStateZip: string;
  noticeEmail: string;
  noticePhone: string;
  employeeSignedName: string;
  schoolName: string;
  laptopMake: string;
  laptopModel: string;
  laptopSerial: string;
  signatureDate: string;
}

const INITIAL_KEYS = [
  'initials1',
  'initials2',
  'initials3',
  'initials4',
  'initials5',
  'initials6',
  'initials7',
  'initials8',
  'initials9',
  'initials10',
  'initials11',
  'initials12',
  'initials14',
];

export const buildLaptopContractPdf = async (
  pdfUrl: string,
  config: any,
  data: LaptopContractData,
  signaturePng?: string,
): Promise<Uint8Array> => {
  const arrayBuffer = await fetchPdfBytes(pdfUrl);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();

  applyEraseRectangles(pages, config.eraseRectangles);

  const fields = config.coordinatesFields || {};

  if (fields.employeeNameHeader)
    drawText(pages[fields.employeeNameHeader.page], data.employeeName, fields.employeeNameHeader, font);

  for (const key of INITIAL_KEYS) {
    const coord = fields[key];
    if (coord) drawText(pages[coord.page], data.initials, coord, font);
  }

  if (fields.noticeName) drawText(pages[fields.noticeName.page], data.employeeName, fields.noticeName, font);
  if (fields.noticeAddress) drawText(pages[fields.noticeAddress.page], data.noticeAddress, fields.noticeAddress, font);
  if (fields.noticeCityStateZip)
    drawText(pages[fields.noticeCityStateZip.page], data.noticeCityStateZip, fields.noticeCityStateZip, font);
  if (fields.noticeEmail) drawText(pages[fields.noticeEmail.page], data.noticeEmail, fields.noticeEmail, font);
  if (fields.noticePhone) drawText(pages[fields.noticePhone.page], data.noticePhone, fields.noticePhone, font);

  if (fields.employeeSignedName)
    drawText(pages[fields.employeeSignedName.page], data.employeeSignedName, fields.employeeSignedName, font);

  if (fields.employeeSignedDate) {
    const date = data.signatureDate ? moment(data.signatureDate).format('MM/DD/YYYY') : moment().format('MM/DD/YYYY');
    drawText(pages[fields.employeeSignedDate.page], date, fields.employeeSignedDate, font);
  }

  if (fields.schoolName) drawText(pages[fields.schoolName.page], data.schoolName, fields.schoolName, font);
  if (fields.laptopMake) drawText(pages[fields.laptopMake.page], data.laptopMake, fields.laptopMake, font);
  if (fields.laptopModel) drawText(pages[fields.laptopModel.page], data.laptopModel, fields.laptopModel, font);
  if (fields.laptopSerial) drawText(pages[fields.laptopSerial.page], data.laptopSerial, fields.laptopSerial, font);

  if (signaturePng) {
    const signPage = pages[config.signPage ?? 2];
    if (signPage) await drawSignature(pdfDoc, signPage, signaturePng, config.coordinates);
  }

  return pdfDoc.save();
};
